import PropTypes from 'prop-types'
import { Fragment, useMemo, useState } from 'react'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import Filter from '../../../Filter/Filter'
import { booleanList, parseToSelect } from '../../MaintenanceRequests/helpers'
import { cutTooLong } from '../../../../helpers/helpers'
import { useGetResmanOptions } from '../hooks'

const STRING_LENGTH = 350

const MaintenanceRequestResmanRequestSummary = ({
  resmanConfig,
  isEditMode,
  data,
  isDisabled,
  description,
  updateMaintenanceRequest,
  setShowDescriptionModal,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const [pets, setPets] = useState(parseToSelect(booleanList).find((pet) => pet.id === data.pets))
  const [enterPermission, setEnterPermission] = useState(
    parseToSelect(booleanList).find(
      (enterPermission) => enterPermission.id === data.enterPermission,
    ),
  )
  const [sourceCategoryId, setSourceCategoryId] = useState(data.sourceCategoryId)
  const [sourceLocationId, setSourceLocationId] = useState(data.sourceLocationId)
  const [areas, setAreas] = useState(
    data.location
      ? data.location.map((location) => ({
          id: location,
          value: location,
          label: location,
        }))
      : [],
  )
  const [priority, setPriority] = useState(data.priority)

  const resmanOptions = useGetResmanOptions(resmanConfig)

  const sourceData = useMemo(() => {
    return {
      source: data.source,
      sourceId: data.sourceId,
    }
  }, [data])

  const onCategoryChange = (categoryId) => {
    updateMaintenanceRequest({
      ...sourceData,
      sourceCategoryId: categoryId,
      category: resmanOptions.categories.find((category) => category.id === categoryId).text,
    })
    setSourceCategoryId(categoryId)
  }

  const onLocationChange = (locationId) => {
    updateMaintenanceRequest({
      ...sourceData,
      sourceLocationId: locationId,
      unit: resmanOptions.locations.find((location) => location.id === locationId).text,
    })
    setSourceLocationId(locationId)
  }

  const onAreasChange = (areas) => {
    updateMaintenanceRequest({ ...sourceData, location: areas.map((area) => area.id) })
    setAreas(areas)
  }

  const onPriorityChange = (priority) => {
    updateMaintenanceRequest({ ...sourceData, priority })
    setPriority(priority)
  }

  return (
    <div className="details-content-block">
      <h4 className="details-content-title details-content-block-title">
        {t('maintenance:requestSummary.title')}
      </h4>
      <div className="details-content-container">
        {Boolean(description) && (
          <div className="details-content-section">
            <h4 className="details-content-title">
              {t('maintenance:requestSummary.fullDescription.title')}
            </h4>
            <p className="details-content-description">{cutTooLong(description, STRING_LENGTH)}</p>
            <button onClick={() => setShowDescriptionModal(true)} className="text-button">
              {t('maintenance:requestSummary.seeMore')}
            </button>
          </div>
        )}
        <div className="details-content-section details-content-section-row">
          <h4 className="details-content-title">
            {t('maintenance:requestSummary.permissionToEnter.title')}
          </h4>
          <p className="details-content-description">
            {isEditMode ? (
              <Filter
                placeholder={
                  enterPermission
                    ? enterPermission.text
                    : t('maintenance:requestSummary.permissionToEnter.placeholder')
                }
                options={parseToSelect(booleanList)}
                value={enterPermission ? [enterPermission] : []}
                name="Permission to Enter"
                variant="rounded"
                disabled={isDisabled}
                closeAfterSelect
                additionalClass="details-filter"
                onChange={(e) => {
                  updateMaintenanceRequest({ enterPermission: e.target.value.id })
                  setEnterPermission(e.target.value)
                }}
              />
            ) : data.enterPermission ? (
              t('common:booleans.yes')
            ) : (
              t('common:booleans.no')
            )}
          </p>
        </div>
        <div className="details-content-section details-content-section-row">
          <h4 className="details-content-title">{t('maintenance:requestSummary.pets.title')}</h4>
          <p className="details-content-description">
            {isEditMode ? (
              <Filter
                placeholder={pets ? pets.text : t('maintenance:requestSummary.pets.placeholder')}
                options={parseToSelect(booleanList)}
                value={pets ? [pets] : []}
                name="Pets"
                disabled={isDisabled}
                closeAfterSelect
                variant="rounded"
                additionalClass="details-filter"
                onChange={(e) => {
                  updateMaintenanceRequest({ pets: e.target.value.id })
                  setPets(e.target.value)
                }}
              />
            ) : data.pets ? (
              t('common:booleans.yes')
            ) : (
              t('common:booleans.no')
            )}
          </p>
        </div>
        <div className="details-content-section details-content-section-row">
          <h4 className="details-content-title">
            {t('maintenance:requestSummary.priority.title')}
          </h4>
          <p className="details-content-description">
            {isEditMode ? (
              <Filter
                placeholder={priority || t('maintenance:requestSummary.priority.placeholder')}
                options={resmanOptions.priorities}
                value={priority ? [priority] : []}
                name="Priority"
                disabled={isDisabled}
                closeAfterSelect
                variant="rounded"
                additionalClass="details-filter"
                onChange={({ target }) => onPriorityChange(target.value.id)}
              />
            ) : (
              <Fragment>
                {data.priority || t('maintenance:requestSummary.priority.fallback')}
              </Fragment>
            )}
          </p>
        </div>
        <div className="details-content-section details-content-section-row">
          <h4 className="details-content-title">
            {t('maintenance:requestSummary.category.title')}
          </h4>
          <p className="details-content-description">
            {isEditMode ? (
              <Filter
                placeholder={data.category || t('maintenance:requestSummary.category.placeholder')}
                options={resmanOptions.categories}
                value={sourceCategoryId ? [sourceCategoryId] : []}
                name="Category"
                disabled={isDisabled}
                closeAfterSelect
                variant="rounded"
                additionalClass="details-filter"
                onChange={({ target }) => onCategoryChange(target.value.id)}
              />
            ) : (
              <Fragment>
                {data.category || t('maintenance:requestSummary.category.fallback')}
              </Fragment>
            )}
          </p>
        </div>
        <div className="details-content-section details-content-section-row">
          <h4 className="details-content-title">
            {t('maintenance:requestSummary.location.title')}
          </h4>
          <p className="details-content-description">
            {isEditMode ? (
              <Filter
                placeholder={data.unit || t('maintenance:requestSummary.location.placeholder')}
                options={resmanOptions.locations}
                value={sourceLocationId ? [sourceLocationId] : []}
                name="Location"
                disabled={isDisabled}
                closeAfterSelect
                variant="rounded"
                additionalClass="details-filter"
                onChange={({ target }) => onLocationChange(target.value.id)}
              />
            ) : (
              <Fragment>{data.unit || t('maintenance:requestSummary.location.fallback')}</Fragment>
            )}
          </p>
        </div>
        <div className="details-content-section details-content-section-row">
          <h4 className="details-content-title">{t('maintenance:requestSummary.areas.title')}</h4>
          <p className="details-content-description">
            {isEditMode ? (
              <Select
                className="select-container"
                classNamePrefix="select-primary"
                options={resmanOptions.areas}
                name="Areas"
                isMulti
                isClearable
                disabled={isDisabled}
                clearValue={() => setAreas([])}
                value={areas}
                additionalClass="details-filter"
                onChange={(areas) => onAreasChange(areas)}
              />
            ) : (
              <Fragment>
                {areas.length > 0
                  ? areas.map((area) => area.label).join(', ')
                  : t('maintenance:requestSummary.areas.fallback')}
              </Fragment>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

MaintenanceRequestResmanRequestSummary.propTypes = {
  resmanConfig: PropTypes.object,
  isEditMode: PropTypes.bool,
  data: PropTypes.object,
  description: PropTypes.string,
  isDisabled: PropTypes.bool,
  updateMaintenanceRequest: PropTypes.func,
  setShowDescriptionModal: PropTypes.func,
}

export default MaintenanceRequestResmanRequestSummary
